// imagepasteUpload — pure helper for the Phase 7 Task 7-8b imagepaste
// confirm flow.
//
// The 3-stage protocol (server side: protocol-events.md §4.3):
//   1. client → server : whiteboard.imagepaste-request   (handled by useCases.sendImagepasteRequest)
//   2. server → client : whiteboard.imagepaste-token     (caller routes here)
//   3. client → S3     : presigned PUT                   (this file)
//   4. client → server : whiteboard.imagepaste-confirm   (this file, on PUT success)
//   5. server → all    : whiteboard.imagepaste broadcast (engine commits + drops preview)
//
// Why a free function: keeps the React layer / SessionProvider thin and the
// actual side-effect (PUT + confirm dispatch) testable in isolation. The
// caller injects `fetchFn` so node-env vitest can run without a real network
// stack and without monkey-patching `globalThis.fetch`.

/**
 * Outcome of `runImagepastePut` — either the PUT + confirm dispatch succeeded
 * (and the broadcast loop is now in flight server-side), or something failed
 * and the caller should drop the local preview / surface a system line.
 */
export type ImagepastePutOutcome =
  | { readonly outcome: 'ok' }
  | {
      readonly outcome: 'put-failed';
      /** HTTP status (only set on a non-ok response, not on network errors). */
      readonly status?: number;
      /** Underlying error (only set on network / abort failures). */
      readonly cause?: unknown;
      /** Human-readable Japanese reason for the system log line. */
      readonly message: string;
    };

/** Minimal `fetch` shape we need (so tests can stub without polyfilling). */
export type ImagepasteFetchFn = (
  url: string,
  init: { method: 'PUT'; body: Blob; headers: Record<string, string>; signal?: AbortSignal },
) => Promise<{ ok: boolean; status: number }>;

export interface RunImagepastePutArgs {
  /** Presigned PUT URL from `whiteboard.imagepaste-token.putUrl`. */
  readonly putUrl: string;
  /** Original `File` selected by the user (kept across the request → token round-trip). */
  readonly file: File | Blob;
  /** Content-Type used when signing the URL — MUST match the request payload. */
  readonly contentType: string;
  readonly fetchFn?: ImagepasteFetchFn;
  readonly signal?: AbortSignal;
}

/**
 * Run the presigned PUT. Returns a discriminated outcome so callers can drop
 * the preview + push a system line on failure.
 *
 * The S3 / MinIO presigned URL is signed with `Content-Type` baked into the
 * signature, so the header MUST be present and identical to what the server
 * passed to the signer. (`Phase 7 Task 7-7` server side enforces it via
 * `imagepasteContentTypeSchema` → presigner.)
 */
export const runImagepastePut = async (
  args: RunImagepastePutArgs,
): Promise<ImagepastePutOutcome> => {
  const fetchImpl = args.fetchFn ?? (defaultFetch as ImagepasteFetchFn);
  let res: { ok: boolean; status: number };
  try {
    res = await fetchImpl(args.putUrl, {
      method: 'PUT',
      body: args.file,
      headers: { 'Content-Type': args.contentType },
      ...(args.signal !== undefined ? { signal: args.signal } : {}),
    });
  } catch (cause) {
    return {
      outcome: 'put-failed',
      cause,
      message: '画像のアップロードに失敗しました (ネットワークエラー)',
    };
  }
  if (!res.ok) {
    return {
      outcome: 'put-failed',
      status: res.status,
      message: `画像のアップロードに失敗しました (HTTP ${res.status})`,
    };
  }
  return { outcome: 'ok' };
};

const defaultFetch = (
  url: string,
  init: { method: 'PUT'; body: Blob; headers: Record<string, string>; signal?: AbortSignal },
): Promise<Response> => {
  const f = globalThis.fetch as ((url: string, init: RequestInit) => Promise<Response>) | undefined;
  if (f === undefined) {
    return Promise.reject(new Error('globalThis.fetch is not available'));
  }
  return f.call(globalThis, url, init as RequestInit);
};
